/**
 * Lookup-table API client (Block 6.B.2 / §G.1).
 *
 * LIST endpoints power the autocomplete pickers (subcategory, specialty,
 * service-area). ADD endpoints power the "+ Add 'X' as new {type}"
 * inline-confirmation per ADR-020 (data-steward role gated server-side
 * via requireDataStewardRole; the frontend gates the affordance via
 * canAddLookup() below to avoid showing a button that always 403s).
 *
 * Provider-level mutations (POST/DELETE
 * /providers/{id}/{lookup-type}) live alongside; the picker calls them
 * to add/remove a lookup association.
 */

import { writeFetch } from './writeFetch';
import type {
  LookupAdminRow,
  HighlightAdminRow,
  LookupRow,
  ProviderServiceAreaExclusion,
  ServiceAreaAdminRow,
  ServiceAreaLookupRow,
  ServiceAreaTiersResponse,
  WhoamiResponse,
} from './types';
import { apiErrorFrom } from './api';

interface ListResponse<T> {
  rows: T[];
  request_id: string;
}

interface AddLookupRequest {
  display_name: string;
  /** Optional override; server defaults to slugify(display_name). */
  slug?: string;
}

interface AddServiceAreaRequest extends AddLookupRequest {
  area_type: 'country' | 'state' | 'county' | 'metro' | 'region' | 'zip';
  state_code: string | null;
  parent_slug: string | null;
}

function tenantUrl(slug: string, path: string): string {
  return `/api/t/${encodeURIComponent(slug)}/v1${path}`;
}

async function getJson<T>(slug: string, path: string, signal?: AbortSignal): Promise<T> {
  const r = await fetch(tenantUrl(slug, path), {
    method: 'GET',
    headers: { Accept: 'application/json' },
    signal,
  });
  if (!r.ok) throw await apiErrorFrom(r, `GET ${path}`);
  return (await r.json()) as T;
}

async function postJson<T>(
  slug: string,
  path: string,
  body: object,
  signal?: AbortSignal,
): Promise<T> {
  const r = await writeFetch(tenantUrl(slug, path), {
    method: 'POST',
    headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
    signal,
  });
  if (!r.ok) throw await apiErrorFrom(r, `POST ${path}`);
  return (await r.json()) as T;
}

async function deleteJson(slug: string, path: string, signal?: AbortSignal): Promise<void> {
  const r = await writeFetch(tenantUrl(slug, path), {
    method: 'DELETE',
    headers: { Accept: 'application/json' },
    signal,
  });
  if (!r.ok) throw await apiErrorFrom(r, `DELETE ${path}`);
}

async function patchJson<T>(
  slug: string,
  path: string,
  body: object,
  signal?: AbortSignal,
): Promise<T> {
  const r = await writeFetch(tenantUrl(slug, path), {
    method: 'PATCH',
    headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
    signal,
  });
  if (!r.ok) throw await apiErrorFrom(r, `PATCH ${path}`);
  return (await r.json()) as T;
}

export const lookupsApi = {
  // ----- Lookup table LIST -----

  listSubcategories(
    slug: string,
    params: { q?: string; limit?: number } = {},
    signal?: AbortSignal,
  ): Promise<ListResponse<LookupRow>> {
    const sp = new URLSearchParams();
    if (params.q) sp.set('q', params.q);
    if (params.limit !== undefined) sp.set('limit', String(params.limit));
    const qs = sp.toString();
    return getJson(slug, `/lookups/subcategories${qs ? `?${qs}` : ''}`, signal);
  },

  listSpecialties(
    slug: string,
    params: { q?: string; limit?: number } = {},
    signal?: AbortSignal,
  ): Promise<ListResponse<LookupRow>> {
    const sp = new URLSearchParams();
    if (params.q) sp.set('q', params.q);
    if (params.limit !== undefined) sp.set('limit', String(params.limit));
    const qs = sp.toString();
    return getJson(slug, `/lookups/specialties${qs ? `?${qs}` : ''}`, signal);
  },

  // Phase 1.9 — mirror of listSpecialties.
  listHighlights(
    slug: string,
    params: { q?: string; limit?: number } = {},
    signal?: AbortSignal,
  ): Promise<ListResponse<LookupRow>> {
    const sp = new URLSearchParams();
    if (params.q) sp.set('q', params.q);
    if (params.limit !== undefined) sp.set('limit', String(params.limit));
    const qs = sp.toString();
    return getJson(slug, `/lookups/highlights${qs ? `?${qs}` : ''}`, signal);
  },

  listServiceAreas(
    slug: string,
    params: {
      q?: string;
      area_type?: 'country' | 'state' | 'county' | 'metro' | 'region' | 'zip';
      state_code?: string;
      parent_slug?: string;
      limit?: number;
    } = {},
    signal?: AbortSignal,
  ): Promise<ListResponse<ServiceAreaLookupRow>> {
    const sp = new URLSearchParams();
    if (params.q) sp.set('q', params.q);
    if (params.area_type) sp.set('area_type', params.area_type);
    if (params.state_code) sp.set('state_code', params.state_code);
    if (params.parent_slug) sp.set('parent_slug', params.parent_slug);
    if (params.limit !== undefined) sp.set('limit', String(params.limit));
    const qs = sp.toString();
    return getJson(slug, `/lookups/service-areas${qs ? `?${qs}` : ''}`, signal);
  },

  // ----- Lookup table ADD (data-steward only; server enforces) -----

  addSubcategory(
    slug: string,
    body: AddLookupRequest,
    signal?: AbortSignal,
  ): Promise<LookupRow> {
    return postJson(slug, '/lookups/subcategories', body, signal);
  },

  addSpecialty(
    slug: string,
    body: AddLookupRequest,
    signal?: AbortSignal,
  ): Promise<LookupRow> {
    return postJson(slug, '/lookups/specialties', body, signal);
  },

  addHighlight(
    slug: string,
    body: AddLookupRequest,
    signal?: AbortSignal,
  ): Promise<LookupRow> {
    return postJson(slug, '/lookups/highlights', body, signal);
  },

  addServiceArea(
    slug: string,
    body: AddServiceAreaRequest,
    signal?: AbortSignal,
  ): Promise<ServiceAreaLookupRow> {
    return postJson(slug, '/lookups/service-areas', body, signal);
  },

  // ----- Provider-level mutations -----

  addProviderSubcategorization(
    slug: string,
    providerId: string,
    body: { slug: string },
    signal?: AbortSignal,
  ): Promise<LookupRow> {
    return postJson(
      slug,
      `/providers/${encodeURIComponent(providerId)}/subcategorizations`,
      body,
      signal,
    );
  },

  removeProviderSubcategorization(
    slug: string,
    providerId: string,
    subcategoryId: string,
    signal?: AbortSignal,
  ): Promise<void> {
    return deleteJson(
      slug,
      `/providers/${encodeURIComponent(providerId)}/subcategorizations/${encodeURIComponent(subcategoryId)}`,
      signal,
    );
  },

  addProviderSpecialization(
    slug: string,
    providerId: string,
    body: { slug: string },
    signal?: AbortSignal,
  ): Promise<LookupRow> {
    return postJson(
      slug,
      `/providers/${encodeURIComponent(providerId)}/specializations`,
      body,
      signal,
    );
  },

  removeProviderSpecialization(
    slug: string,
    providerId: string,
    specialtyId: string,
    signal?: AbortSignal,
  ): Promise<void> {
    return deleteJson(
      slug,
      `/providers/${encodeURIComponent(providerId)}/specializations/${encodeURIComponent(specialtyId)}`,
      signal,
    );
  },

  addProviderServiceArea(
    slug: string,
    providerId: string,
    body:
      | { slug: string }
      | { tier: 'msa'; msa_id: string }
      | { tier: 'metro_division'; md_id: string }
      | { tier: 'csa'; csa_id: string }
      | { tier: 'zip'; zip_id: string },
    signal?: AbortSignal,
  ): Promise<{ id: string; tier: string; fk_id?: string; service_area_id?: string }> {
    return postJson(
      slug,
      `/providers/${encodeURIComponent(providerId)}/service-areas`,
      body,
      signal,
    );
  },

  /**
   * Phase 1.8 — DELETE accepts the provider_service_areas row uuid (not
   * the lookup row id). Callers pass the row's `id` field from
   * ProviderServiceArea.
   */
  removeProviderServiceArea(
    slug: string,
    providerId: string,
    rowId: string,
    signal?: AbortSignal,
  ): Promise<void> {
    return deleteJson(
      slug,
      `/providers/${encodeURIComponent(providerId)}/service-areas/${encodeURIComponent(rowId)}`,
      signal,
    );
  },

  // ----- Phase 1.8: service-area tier-table loaders + exclusions -----

  /**
   * Bulk-load all tier tables (CSAs + MSAs + MDs + ZIPs). Single endpoint
   * for picker initialization on detail pages. ~10KB at NJ scale.
   */
  listServiceAreaTiers(slug: string, signal?: AbortSignal): Promise<ServiceAreaTiersResponse> {
    return getJson(slug, `/service-area-tiers`, signal);
  },

  listProviderServiceAreaExclusions(
    slug: string,
    providerId: string,
    signal?: AbortSignal,
  ): Promise<{ rows: ProviderServiceAreaExclusion[]; request_id: string }> {
    return getJson(slug, `/providers/${encodeURIComponent(providerId)}/service-area-exclusions`, signal);
  },

  addProviderServiceAreaExclusion(
    slug: string,
    providerId: string,
    rowId: string,
    body: { excluded_zip_id: string },
    signal?: AbortSignal,
  ): Promise<{ added: boolean }> {
    return postJson(
      slug,
      `/providers/${encodeURIComponent(providerId)}/service-areas/${encodeURIComponent(rowId)}/exclusions`,
      body,
      signal,
    );
  },

  removeProviderServiceAreaExclusion(
    slug: string,
    providerId: string,
    rowId: string,
    excludedZipId: string,
    signal?: AbortSignal,
  ): Promise<void> {
    return deleteJson(
      slug,
      `/providers/${encodeURIComponent(providerId)}/service-areas/${encodeURIComponent(rowId)}/exclusions/${encodeURIComponent(excludedZipId)}`,
      signal,
    );
  },

  // ----- Block 6.C.1: Admin LIST (with include_inactive + with_record_counts) -----
  //
  // §m: these endpoints share routes with the picker LIST endpoints above.
  // The picker callers don't pass either flag and inherit defaults
  // (include_inactive=false, with_record_counts=false). Admin callers pass
  // both flags. Server-side defaults match this client (see
  // api/_shared/repos/lookups.ts §m discipline on listSubcategories).

  listAdminSubcategories(
    slug: string,
    params: { q?: string; limit?: number; include_inactive?: boolean } = {},
    signal?: AbortSignal,
  ): Promise<ListResponse<LookupAdminRow>> {
    const sp = new URLSearchParams();
    if (params.q) sp.set('q', params.q);
    if (params.limit !== undefined) sp.set('limit', String(params.limit));
    if (params.include_inactive) sp.set('include_inactive', 'true');
    sp.set('with_record_counts', 'true');
    return getJson(slug, `/lookups/subcategories?${sp.toString()}`, signal);
  },

  listAdminSpecialties(
    slug: string,
    params: { q?: string; limit?: number; include_inactive?: boolean } = {},
    signal?: AbortSignal,
  ): Promise<ListResponse<LookupAdminRow>> {
    const sp = new URLSearchParams();
    if (params.q) sp.set('q', params.q);
    if (params.limit !== undefined) sp.set('limit', String(params.limit));
    if (params.include_inactive) sp.set('include_inactive', 'true');
    sp.set('with_record_counts', 'true');
    return getJson(slug, `/lookups/specialties?${sp.toString()}`, signal);
  },

  listAdminHighlights(
    slug: string,
    params: { q?: string; limit?: number; include_inactive?: boolean } = {},
    signal?: AbortSignal,
  ): Promise<ListResponse<HighlightAdminRow>> {
    const sp = new URLSearchParams();
    if (params.q) sp.set('q', params.q);
    if (params.limit !== undefined) sp.set('limit', String(params.limit));
    if (params.include_inactive) sp.set('include_inactive', 'true');
    sp.set('with_record_counts', 'true');
    return getJson(slug, `/lookups/highlights?${sp.toString()}`, signal);
  },

  // Phase 1.10 — Service Model admin list. Read-side only for Part B;
  // admin CRUD (add / deprecate / merge) is a follow-up sub-block.
  listAdminServiceModels(
    slug: string,
    params: { q?: string; limit?: number; include_inactive?: boolean } = {},
    signal?: AbortSignal,
  ): Promise<ListResponse<LookupAdminRow>> {
    const sp = new URLSearchParams();
    if (params.q) sp.set('q', params.q);
    if (params.limit !== undefined) sp.set('limit', String(params.limit));
    if (params.include_inactive) sp.set('include_inactive', 'true');
    sp.set('with_record_counts', 'true');
    return getJson(slug, `/lookups/service-models?${sp.toString()}`, signal);
  },

  listAdminServiceAreas(
    slug: string,
    params: {
      q?: string;
      area_type?: 'country' | 'state' | 'county' | 'metro' | 'region' | 'zip';
      state_code?: string;
      parent_slug?: string;
      limit?: number;
      include_inactive?: boolean;
    } = {},
    signal?: AbortSignal,
  ): Promise<ListResponse<ServiceAreaAdminRow>> {
    const sp = new URLSearchParams();
    if (params.q) sp.set('q', params.q);
    if (params.area_type) sp.set('area_type', params.area_type);
    if (params.state_code) sp.set('state_code', params.state_code);
    if (params.parent_slug) sp.set('parent_slug', params.parent_slug);
    if (params.limit !== undefined) sp.set('limit', String(params.limit));
    if (params.include_inactive) sp.set('include_inactive', 'true');
    sp.set('with_record_counts', 'true');
    return getJson(slug, `/lookups/service-areas?${sp.toString()}`, signal);
  },

  // ----- Block 6.C.1: Admin write paths (data-steward gated server-side) -----

  // PATCH — exactly one of { display_name } | { is_active } per ADR-020 §3
  // audit-row-per-logical-operation discipline (see lookups.ts handler header §m).

  patchSubcategory(
    slug: string,
    id: string,
    body: { display_name: string } | { is_active: boolean },
    signal?: AbortSignal,
  ): Promise<LookupRow> {
    return patchJson(slug, `/lookups/subcategories/${encodeURIComponent(id)}`, body, signal);
  },

  patchSpecialty(
    slug: string,
    id: string,
    body: { display_name: string } | { is_active: boolean },
    signal?: AbortSignal,
  ): Promise<LookupRow> {
    return patchJson(slug, `/lookups/specialties/${encodeURIComponent(id)}`, body, signal);
  },

  patchHighlight(
    slug: string,
    id: string,
    body: { display_name: string } | { is_active: boolean },
    signal?: AbortSignal,
  ): Promise<LookupRow> {
    return patchJson(slug, `/lookups/highlights/${encodeURIComponent(id)}`, body, signal);
  },

  patchServiceArea(
    slug: string,
    id: string,
    body: { display_name: string } | { is_active: boolean },
    signal?: AbortSignal,
  ): Promise<ServiceAreaLookupRow> {
    return patchJson(slug, `/lookups/service-areas/${encodeURIComponent(id)}`, body, signal);
  },

  // POST /rename-slug — per-form Save grammar (ADR-024 D-affiliation-editor-shape).

  renameSubcategorySlug(
    slug: string,
    id: string,
    body: { new_slug: string },
    signal?: AbortSignal,
  ): Promise<LookupRow> {
    return postJson(
      slug,
      `/lookups/subcategories/${encodeURIComponent(id)}/rename-slug`,
      body,
      signal,
    );
  },

  renameSpecialtySlug(
    slug: string,
    id: string,
    body: { new_slug: string },
    signal?: AbortSignal,
  ): Promise<LookupRow> {
    return postJson(
      slug,
      `/lookups/specialties/${encodeURIComponent(id)}/rename-slug`,
      body,
      signal,
    );
  },

  renameHighlightSlug(
    slug: string,
    id: string,
    body: { new_slug: string },
    signal?: AbortSignal,
  ): Promise<LookupRow> {
    return postJson(
      slug,
      `/lookups/highlights/${encodeURIComponent(id)}/rename-slug`,
      body,
      signal,
    );
  },

  renameServiceAreaSlug(
    slug: string,
    id: string,
    body: { new_slug: string },
    signal?: AbortSignal,
  ): Promise<ServiceAreaLookupRow> {
    return postJson(
      slug,
      `/lookups/service-areas/${encodeURIComponent(id)}/rename-slug`,
      body,
      signal,
    );
  },

  // POST /merge — subcategories + specialties only (ADR-020 §1; service-areas defers).

  mergeSubcategories(
    slug: string,
    body: { from_id: string; into_id: string; expected_record_count: number },
    signal?: AbortSignal,
  ): Promise<{ merged_into: LookupRow; records_merged: number }> {
    return postJson(slug, `/lookups/subcategories/merge`, body, signal);
  },

  mergeSpecialties(
    slug: string,
    body: { from_id: string; into_id: string; expected_record_count: number },
    signal?: AbortSignal,
  ): Promise<{ merged_into: LookupRow; records_merged: number }> {
    return postJson(slug, `/lookups/specialties/merge`, body, signal);
  },

  mergeHighlights(
    slug: string,
    body: { from_id: string; into_id: string; expected_record_count: number },
    signal?: AbortSignal,
  ): Promise<{ merged_into: LookupRow; records_merged: number }> {
    return postJson(slug, `/lookups/highlights/merge`, body, signal);
  },
};

// ---------------------------------------------------------------------
// Role gating for the "+ Add as new" picker affordance.
// ---------------------------------------------------------------------
//
// Phase 1.11 — consolidated. The prior `DATA_STEWARD_ROLES` constant +
// inline includes() check at this site duplicated the server's role
// list; both removed in favor of routing through `canManageLookups`
// from src/lib/capabilities.ts (the single frontend mirror of the
// role × capability matrix). `canAddLookup` is preserved as the
// export name so the LookupPicker call sites (and any tests) don't
// churn; internally it delegates to `canManageLookups` which reads
// from `ROLE_CAPABILITIES[role].has('manage_lookups')`.
//
// Today's set unchanged: owner + data_steward. Forward semantic: if
// a future role earns `manage_lookups` in the matrix, the "+ Add as
// new" affordance lights up for that role automatically.
import { canManageLookups } from './capabilities';

/**
 * Returns true if the current user can add new lookup rows. Frontend
 * affordance gate for the LookupPicker "+ Add as new" surface (Block
 * 6.B.2 / Q6).
 *
 * The server enforces independently via `requireCapability('manage_lookups')`
 * at the lookups handlers — this gate is UX (avoids surfacing a button
 * that always 403s). A non-steward UI bypass that POSTs anyway gets 403
 * from the handler.
 *
 * Phase 1.11: delegates to `canManageLookups(whoami)` from
 * src/lib/capabilities.ts. The function name + signature preserved for
 * backward-compat with existing LookupPicker call sites.
 */
export function canAddLookup(whoami: WhoamiResponse | null): boolean {
  return canManageLookups(whoami);
}

/**
 * Mirror of `slugify` in `api/_shared/repos/lookups.ts:73`. Used by the
 * picker to preview the auto-generated slug as the user types
 * display_name in the "+ Add" affordance (Q2 disposition).
 *
 * §m: keep in sync with the server's slugify; same Phase 1 boundary
 * discipline rationale as DATA_STEWARD_ROLES above.
 */
export function slugify(displayName: string): string {
  return displayName
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}
